import type {
  SiteConductBlock,
  SiteContent,
  SiteFooter,
  SiteFooterColumn,
  SiteFooterColumnId,
  SiteFooterLink,
  SiteIdentity,
  SiteRulesSection,
  SiteStaffJoinSection,
  SiteStaffJoinStep,
  SiteStaffJoinStepIcon,
  SiteStaffLearnMoreSection,
  SiteStaffMember,
  SiteStaffRole,
  SiteStaffXatRank,
} from '@/lib/cms-types';
import { STAFF_XAT_RANK_PRESETS } from '@/lib/equipe-xat-ranks';
import { CONDUCT_ITEMS_SEED, RULES_SEED } from '@/lib/regras-seed-data';

const FOOTER_COLUMN_IDS: SiteFooterColumnId[] = ['product', 'support', 'community', 'legal'];

export const DEFAULT_FOOTER_PEEK_SMILIES = [
  'tongue',
  'redface',
  'wink',
  'cool',
  'cd',
] as const;

export function normalizePeekSmilies(raw: unknown): SiteFooter['peekSmilies'] {
  const defaults = [...DEFAULT_FOOTER_PEEK_SMILIES] as SiteFooter['peekSmilies'];
  if (!Array.isArray(raw)) return defaults;
  return defaults.map((fallback, i) => {
    const value = raw[i];
    if (typeof value !== 'string') return fallback;
    const cleaned = value.trim().toLowerCase().replace(/[^a-z0-9_]/g, '');
    return cleaned || fallback;
  }) as SiteFooter['peekSmilies'];
}

export function buildDefaultIdentity(): SiteIdentity {
  return {
    title: 'xat Ajuda',
    subtitle: 'Central de Suporte',
    faviconUrl: '',
  };
}

export function normalizeIdentity(patch?: Partial<SiteIdentity> | null): SiteIdentity {
  const b = buildDefaultIdentity();
  if (!patch) return b;
  return {
    title: typeof patch.title === 'string' && patch.title.trim() ? patch.title.trim() : b.title,
    subtitle: typeof patch.subtitle === 'string' ? patch.subtitle.trim() : b.subtitle,
    faviconUrl: typeof patch.faviconUrl === 'string' ? patch.faviconUrl.trim() : b.faviconUrl,
  };
}

export function buildDefaultFooter(): SiteFooter {
  return {
    brandName: 'xatAjuda.com',
    brandDescription:
      'Encontre respostas, explore guias e obtenha suporte para tudo no xat.',
    columns: [
      {
        id: 'product',
        title: 'Produto',
        links: [
          { label: 'Chats', href: '#' },
          { label: 'Powers', href: '#' },
          { label: 'Loja', href: '#' },
        ],
      },
      {
        id: 'support',
        title: 'Suporte',
        links: [
          { label: 'xat Ajuda', href: '#' },
          { label: 'Contato', href: '#' },
          { label: 'Denunciar problema', href: '#' },
        ],
      },
      {
        id: 'community',
        title: 'Comunidade',
        links: [
          { label: 'Diretrizes', href: '#' },
          { label: 'Segurança', href: '#' },
          { label: 'Código de Conduta', href: '#' },
        ],
      },
      {
        id: 'legal',
        title: 'Legal',
        links: [
          { label: 'Termos', href: '/termos' },
          { label: 'Privacidade', href: '/privacidade' },
        ],
      },
    ],
    appsSectionTitle: 'Use o xat no celular',
    appStoreUrl: 'https://apps.apple.com/br/app/xat/id1003294391',
    googlePlayUrl: 'https://play.google.com/store/apps/details?id=com.xat.app&hl=pt_BR',
    appStoreAriaLabel: 'Baixar na App Store',
    googlePlayAriaLabel: 'Baixar no Google Play',
    copyright: '© 2026 xat Ajuda. Todos os direitos reservados.',
    disclaimer:
      'As marcas, nomes, powers, smilies, imagens e demais elementos relacionados ao xat são de propriedade do xat.com Ltd. e são utilizados com autorização. É vedada sua reprodução, distribuição ou exploração comercial sem a autorização do respectivo titular.',
    changelog: {
      prefix: 'xatAjuda.com',
      version: '',
      href: '/changelog?produto=ajuda',
    },
    peekSmilies: [...DEFAULT_FOOTER_PEEK_SMILIES],
  };
}

function normalizeFooterLink(link: Partial<SiteFooterLink> | null | undefined): SiteFooterLink {
  const label = typeof link?.label === 'string' ? link.label : '';
  let href = typeof link?.href === 'string' ? link.href : '#';

  // Migra placeholders antigos da coluna Legal para as páginas públicas.
  const labelKey = label.trim().toLowerCase();
  if (!href || href === '#') {
    if (labelKey === 'termos' || labelKey === 'termos de uso') href = '/termos';
    if (labelKey === 'privacidade' || labelKey === 'política de privacidade' || labelKey === 'politica de privacidade') {
      href = '/privacidade';
    }
  }

  return { label, href };
}

function normalizeFooterColumn(
  id: SiteFooterColumnId,
  patch: Partial<SiteFooterColumn> | null | undefined,
  fallback: SiteFooterColumn
): SiteFooterColumn {
  const linksFromPatch = patch?.links;
  const linksRaw = Array.isArray(linksFromPatch) ? linksFromPatch : fallback.links;
  return {
    id,
    title: typeof patch?.title === 'string' && patch.title.length > 0 ? patch.title : fallback.title,
    links: linksRaw.map((l) => normalizeFooterLink(l)),
  };
}

export function normalizeFooter(patch?: Partial<SiteFooter> | null): SiteFooter {
  const b = buildDefaultFooter();
  if (!patch) return b;

  const byId = new Map<string, Partial<SiteFooterColumn>>();
  if (Array.isArray(patch.columns)) {
    for (const col of patch.columns) {
      if (col && typeof col === 'object' && typeof col.id === 'string') {
        byId.set(col.id, col);
      }
    }
  }

  const base = buildDefaultFooter();
  const patchDisclaimer =
    typeof patch.disclaimer === 'string' ? patch.disclaimer.trim() : '';
  const legacyDisclaimer =
    !patchDisclaimer ||
    /^não somos afiliados a xat\.com ltd\.?$/i.test(patchDisclaimer);

  const patchPrefix =
    typeof patch.changelog?.prefix === 'string' ? patch.changelog.prefix.trim() : '';
  const legacyPrefix =
    !patchPrefix ||
    /^xat\s*ajuda$/i.test(patchPrefix) ||
    /^xatajuda\.com$/i.test(patchPrefix);

  const patchHref =
    typeof patch.changelog?.href === 'string' ? patch.changelog.href.trim() : '';
  const legacyHref =
    !patchHref || patchHref === '/changelog' || patchHref === '/changelog/';

  return {
    brandName: typeof patch.brandName === 'string' ? patch.brandName : b.brandName,
    brandDescription:
      typeof patch.brandDescription === 'string' ? patch.brandDescription : b.brandDescription,
    columns: FOOTER_COLUMN_IDS.map((id) => {
      const fallback = b.columns.find((c) => c.id === id)!;
      return normalizeFooterColumn(id, byId.get(id), fallback);
    }),
    appStoreUrl: typeof patch.appStoreUrl === 'string' ? patch.appStoreUrl : b.appStoreUrl,
    googlePlayUrl: typeof patch.googlePlayUrl === 'string' ? patch.googlePlayUrl : b.googlePlayUrl,
    appsSectionTitle:
      typeof patch.appsSectionTitle === 'string' && patch.appsSectionTitle.trim()
        ? patch.appsSectionTitle
        : b.appsSectionTitle,
    appStoreAriaLabel:
      typeof patch.appStoreAriaLabel === 'string' ? patch.appStoreAriaLabel : b.appStoreAriaLabel,
    googlePlayAriaLabel:
      typeof patch.googlePlayAriaLabel === 'string'
        ? patch.googlePlayAriaLabel
        : b.googlePlayAriaLabel,
    copyright: typeof patch.copyright === 'string' ? patch.copyright : b.copyright,
    disclaimer: legacyDisclaimer ? base.disclaimer : patchDisclaimer,
    changelog: {
      prefix: legacyPrefix ? base.changelog.prefix : patchPrefix,
      version:
        typeof patch.changelog?.version === 'string' ? patch.changelog.version : b.changelog.version,
      href: legacyHref ? base.changelog.href : patchHref,
    },
    peekSmilies: normalizePeekSmilies(patch.peekSmilies ?? b.peekSmilies),
  };
}

function escapeHtml(text: string) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function paragraphsToHtml(paragraphs: string[]) {
  return paragraphs.map((t) => `<p>${escapeHtml(t)}</p>`).join('');
}

function rulesSeedToSections(): SiteRulesSection[] {
  return RULES_SEED.map((r) => ({
    id: `rule-${r.id}`,
    displayOrder: r.id,
    title: r.title,
    category: r.category,
    updated: r.updated,
    bodyHtml: paragraphsToHtml(r.content),
  }));
}

function conductSeed(): SiteConductBlock {
  return {
    badgeLabel: 'Exclusivo para a Equipe',
    title: 'Código de Conduta',
    subtitle:
      'Diretrizes internas destinadas aos membros da equipe de moderação e proprietários do chat Ajuda.',
    items: CONDUCT_ITEMS_SEED.map((text, i) => ({
      id: `conduct-${i + 1}`,
      bodyHtml: `<p>${escapeHtml(text)}</p>`,
    })),
  };
}

export function normalizeStaffRole(input: unknown): SiteStaffRole {
  const r = String(input ?? '').trim();
  const key = r.toLowerCase();
  if (r === 'mainOwner' || key === 'mainowner') return 'mainOwner';
  if (r === 'owners' || key === 'owners' || key === 'owner') return 'owners';
  if (r === 'moderadores' || key === 'moderadores' || key === 'moderador') return 'moderadores';
  if (r === 'membros' || key === 'membros' || key === 'membro') return 'membros';
  const aliases: Record<string, SiteStaffRole> = {
    'main owners': 'mainOwner',
    'main owner': 'mainOwner',
  };
  return aliases[key] ?? 'membros';
}

export function normalizeSiteStaffMember(
  m: Partial<SiteStaffMember> & { id?: string; role?: unknown },
  indexFallback: number
): SiteStaffMember {
  const role = normalizeStaffRole(m.role);
  const id =
    typeof m.id === 'string' && m.id.length > 0 ? m.id : `staff-seed-${indexFallback + 1}`;
  const username = typeof m.username === 'string' ? m.username : '';
  const presetRank = STAFF_XAT_RANK_PRESETS[username.trim().toLowerCase()];
  const xatRank: SiteStaffXatRank | undefined =
    m.xatRank?.label && m.xatRank.color
      ? m.xatRank
      : presetRank;

  return {
    id,
    name: typeof m.name === 'string' ? m.name : '',
    username,
    xatId: typeof m.xatId === 'string' ? m.xatId : '',
    country: m.country === 'PT' ? 'PT' : 'BR',
    avatar: typeof m.avatar === 'string' ? m.avatar : '',
    autoToonFromXatId: Boolean(m.autoToonFromXatId),
    role,
    ...(xatRank ? { xatRank } : {}),
    ...(typeof m.description === 'string' && m.description.length > 0
      ? { description: m.description }
      : {}),
  };
}

/** Lista inicial alinhada à página /equipe (dados dos cartões). */
function staffSeed(): SiteStaffMember[] {
  const rows: Omit<SiteStaffMember, 'id'>[] = [
    { name: 'Cupim', username: 'Cupim', xatId: '11011', country: 'BR', avatar: 'C', role: 'mainOwner', xatRank: STAFF_XAT_RANK_PRESETS.cupim },
    { name: 'Guinho', username: 'Guinho', xatId: '2300000', country: 'BR', avatar: 'G', role: 'mainOwner', xatRank: STAFF_XAT_RANK_PRESETS.guinho },
    { name: 'Thuk', username: 'Asta', xatId: '91667566', country: 'BR', avatar: '171', role: 'mainOwner' },
    {
      name: 'Beetlejuice',
      username: 'beetLejuicedj',
      xatId: '88801017',
      country: 'PT',
      avatar: 'B',
      role: 'owners',
    },
    { name: 'Lion', username: 'Lion', xatId: '112233', country: 'BR', avatar: 'L', role: 'owners' },
    { name: 'Luig', username: 'Luig', xatId: '10000270', country: 'BR', avatar: 'L', role: 'owners', xatRank: STAFF_XAT_RANK_PRESETS.luig },
    {
      name: 'Manuel',
      username: 'Cobradeserto',
      xatId: '1348873407',
      country: 'PT',
      avatar: 'M',
      role: 'owners',
    },
    {
      name: 'Artemis',
      username: 'KarinaCobain',
      xatId: '1372871486',
      country: 'BR',
      avatar: 'A',
      role: 'moderadores',
    },
    {
      name: 'Banky',
      username: 'BankyBanky',
      xatId: '1554566988',
      country: 'BR',
      avatar: 'B',
      role: 'moderadores',
    },
    {
      name: 'Iguz',
      username: 'Iguz',
      xatId: '1483158164',
      country: 'BR',
      avatar: 'I',
      role: 'membros',
      autoToonFromXatId: true,
    },
    { name: 'Mitski', username: 'Mitski', xatId: '1549073760', country: 'BR', avatar: 'M', role: 'membros' },
  ];
  return rows.map((r, i) => ({ ...r, id: `staff-seed-${i + 1}` }));
}

function staffRoleDescriptionsSeed(): Record<SiteStaffRole, string> {
  return {
    mainOwner:
      'Responsáveis pela gestão geral do chat e do site, incluindo decisões administrativas, estratégicas e técnicas.\n\nTambém atuam como ponte principal entre os usuários e a equipe, garantindo que tudo funcione de forma alinhada.',
    owners:
      'Cuidam da coordenação geral do chat e da equipe.\n\nTrabalham junto aos moderadores e membros, ajudando a manter a organização, o bom funcionamento e a comunicação com os usuários.',
    moderadores:
      'Responsáveis por manter a ordem no chat e dar suporte aos usuários.\n\nAtuam diretamente na moderação, garantindo que as regras sejam seguidas e que o ambiente continue saudável para todos.',
    membros:
      'Ajudam no suporte geral dentro do chat, tirando dúvidas e orientando usuários.\n\nUsuários que se destacam podem receber oportunidades para auxiliar na moderação temporariamente e evoluir dentro da equipe.',
  };
}

const JOIN_STEP_ICONS: SiteStaffJoinStepIcon[] = [
  'Compass',
  'HeartHandshake',
  'ShieldCheck',
  'MessagesSquare',
  'TrendingUp',
  'Sparkles',
  'Users',
  'Handshake',
];

function staffJoinSectionSeed(): SiteStaffJoinSection {
  return {
    badgeLabel: 'Faça parte da equipe',
    title: 'Como se tornar membro da nossa equipe',
    subtitle:
      'Na prática, o que importa são hábitos positivos na comunidade, respeito às regras e contribuições consistentes ao longo do tempo — antes de qualquer convite oficial.',
    steps: [
      {
        id: 'join-step-1',
        title: 'Um caminho natural',
        text: 'Entrar para a equipe do xat Ajuda não é algo que você compra ou ganha em concurso. Isso acontece naturalmente com o tempo, através da sua dedicação e da forma como você ajuda a comunidade.',
        icon: 'Compass',
      },
      {
        id: 'join-step-2',
        title: 'Ajude de verdade',
        text: 'O principal é simples: ajudar de verdade. Responder dúvidas, apoiar usuários com problemas e estar presente no chat já faz toda a diferença.',
        icon: 'HeartHandshake',
      },
      {
        id: 'join-step-3',
        title: 'Respeite as regras',
        text: 'Também é importante seguir as regras. Quem não respeita o ambiente dificilmente será considerado para um cargo dentro da equipe.',
        icon: 'ShieldCheck',
      },
      {
        id: 'join-step-4',
        title: 'Comunicação conta',
        text: 'Ser ativo, ter uma boa comunicação e saber lidar com as pessoas conta muito. A forma como você interage com os outros mostra se você está preparado para algo maior.',
        icon: 'MessagesSquare',
      },
      {
        id: 'join-step-5',
        title: 'Mostre seu potencial',
        text: 'Com o tempo, quem se destaca pode receber oportunidades, como ajudar na moderação temporariamente. E é aí que você pode mostrar ainda mais seu potencial.',
        icon: 'TrendingUp',
      },
    ],
  };
}

function staffLearnMoreSeed(): SiteStaffLearnMoreSection {
  return {
    title: 'Quer saber mais?',
    description:
      'Conheça as regras da comunidade e o código de conduta — a base de tudo o que fazemos no xat Ajuda.',
    regrasButtonLabel: 'Ver regras',
    condutaButtonLabel: 'Código de Conduta',
  };
}

function normalizeJoinStepIcon(raw: unknown): SiteStaffJoinStepIcon {
  const value = String(raw ?? '').trim();
  return JOIN_STEP_ICONS.includes(value as SiteStaffJoinStepIcon)
    ? (value as SiteStaffJoinStepIcon)
    : 'Compass';
}

export function normalizeStaffJoinSection(
  raw?: Partial<SiteStaffJoinSection> | null
): SiteStaffJoinSection {
  const base = staffJoinSectionSeed();
  if (!raw) return base;
  const stepsRaw = Array.isArray(raw.steps) ? raw.steps : base.steps;
  const steps: SiteStaffJoinStep[] = stepsRaw.map((step, i) => {
    const fallback = base.steps[i] ?? base.steps[0];
    return {
      id: String(step?.id ?? fallback.id).trim() || `join-step-${i + 1}`,
      title: String(step?.title ?? fallback.title).trim() || fallback.title,
      text: String(step?.text ?? fallback.text).trim() || fallback.text,
      icon: normalizeJoinStepIcon(step?.icon ?? fallback.icon),
    };
  });
  return {
    badgeLabel: String(raw.badgeLabel ?? base.badgeLabel).trim() || base.badgeLabel,
    title: String(raw.title ?? base.title).trim() || base.title,
    subtitle: String(raw.subtitle ?? base.subtitle).trim() || base.subtitle,
    steps: steps.length > 0 ? steps : base.steps,
  };
}

export function normalizeStaffLearnMore(
  raw?: Partial<SiteStaffLearnMoreSection> | null
): SiteStaffLearnMoreSection {
  const base = staffLearnMoreSeed();
  if (!raw) return base;
  return {
    title: String(raw.title ?? base.title).trim() || base.title,
    description: String(raw.description ?? base.description).trim() || base.description,
    regrasButtonLabel:
      String(raw.regrasButtonLabel ?? base.regrasButtonLabel).trim() || base.regrasButtonLabel,
    condutaButtonLabel:
      String(raw.condutaButtonLabel ?? base.condutaButtonLabel).trim() || base.condutaButtonLabel,
  };
}

export function buildDefaultSiteContent(): SiteContent {
  return {
    hero: {
      title: 'Como podemos ajudar?',
      description:
        'Respostas rápidas, guias detalhados e suporte completo para todas as funcionalidades do xat.',
      primaryButtonLabel: 'Como criar conta',
      secondaryButtonLabel: 'Redefinir senha',
      primarySearchQuery: 'conta',
      secondarySearchQuery: 'senha',
    },
    rulesPageTitle: 'Regras do xat',
    rulesPageSubtitle:
      'Diretrizes essenciais para manter nossa comunidade segura, respeitosa e agradável para todos os usuários.',
    rulesSections: rulesSeedToSections(),
    conduct: conductSeed(),
    staffPageTitle: 'Nossa Equipe',
    staffPageSubtitle:
      'Conheça as pessoas dedicadas que mantêm a comunidade xat segura, ativa e acolhedora.',
    staffMembers: staffSeed(),
    staffRoleDescriptions: staffRoleDescriptionsSeed(),
    staffJoinSection: staffJoinSectionSeed(),
    staffLearnMore: staffLearnMoreSeed(),
    footer: buildDefaultFooter(),
    identity: buildDefaultIdentity(),
  };
}

export function mergeSiteWithDefaults(patch?: Partial<SiteContent> | null): SiteContent {
  const b = buildDefaultSiteContent();
  if (!patch) return b;
  return {
    ...b,
    ...patch,
    hero: { ...b.hero, ...patch.hero },
    conduct: {
      ...b.conduct,
      ...patch.conduct,
      items: patch.conduct?.items ?? b.conduct.items,
    },
    rulesSections: patch.rulesSections ?? b.rulesSections,
    staffMembers: (patch.staffMembers ?? b.staffMembers).map((row, i) =>
      normalizeSiteStaffMember(row, i)
    ),
    staffRoleDescriptions: {
      ...b.staffRoleDescriptions,
      ...(patch.staffRoleDescriptions ?? {}),
    },
    staffJoinSection: normalizeStaffJoinSection(patch.staffJoinSection ?? b.staffJoinSection),
    staffLearnMore: normalizeStaffLearnMore(patch.staffLearnMore ?? b.staffLearnMore),
    footer: normalizeFooter(patch.footer ?? b.footer),
    identity: normalizeIdentity(patch.identity ?? b.identity),
  };
}
